import { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { apiError, showApiError } from "@/lib/api-error";

/** The recipe page's reads and writes (POS2-1456). */

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
/** Catalogue search asks once typing pauses, not once per letter (POS2-1467). */
const SEARCH_DEBOUNCE_MS = 250;

export type Unit = "g" | "kg" | "ml" | "L" | "each";

export interface MethodStep {
  step: number;
  /** `{n}` stands for line n's name. */
  text: string;
  ingredient_refs: number[];
}

/** One ingredient line, its quantity for the recipe's whole yield. */
export interface RecipeLine {
  id: string;
  inventory_item_id: string | null;
  quantity: number;
  unit_of_measure: Unit;
  /** What an unmatched line is called. */
  description: string | null;
  item_name: string | null;
  item_price_per_kg: number | null;
  cost: { amount: number | null } | null;
  /** Weight for the whole yield; null for an each with no pack weight. */
  weight_kg: number | null;
}

export interface Recipe {
  id: string;
  name: string;
  category_slug: string | null;
  yield_portions: number;
  method: string | null;
  method_steps: MethodStep[] | null;
  ingredients: RecipeLine[];
}

/** A menu the dish is on at the site, with its row there and that row's planned covers. */
export interface DishMenu {
  menu_id: string;
  recipe_id: string;
  planned_covers: number | null;
}

export interface InventoryItem {
  id: string;
  description: string;
  simple_name: string | null;
  clean_name: string | null;
  price_per_kg: number | null;
}

/** A line picked from the catalogue: its quantity for the whole yield, in the unit it is stored in. */
export interface NewLine {
  inventory_item_id: string;
  quantity: number;
  unit_of_measure: Unit;
}

export interface DishSave {
  site_id: string;
  category_slug: string | null;
  method_steps: MethodStep[];
  /** The lines that stay; one left out is removed. */
  lines: { id: string; quantity: number; inventory_item_id: string | null }[];
  added: NewLine[];
  covers: { menu_id: string; planned_covers: number | null }[];
}

export interface DishAdd {
  site_id: string;
  name: string;
  category_slug: string | null;
  method_steps: MethodStep[];
  /** For one portion: a new dish yields one. */
  lines: NewLine[];
  covers: { menu_id: string; planned_covers: number | null }[];
}

export function useRecipe(recipeId: string) {
  const headers = useApiHeaders();
  const { t } = useTranslation();
  return useQuery<Recipe>({
    queryKey: ["recipe", recipeId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/recipes/${recipeId}`, { headers });
      if (!res.ok) throw await apiError(t, res);
      return (await res.json()).data;
    },
  });
}

/** The site's menus the recipe's dish is on. Under "menus", so menu writes refresh it. */
export function useDishMenus(recipeId: string, siteId: string) {
  const headers = useApiHeaders();
  const { t } = useTranslation();
  return useQuery<DishMenu[]>({
    queryKey: ["menus", siteId, "dish", recipeId],
    queryFn: async () => {
      const params = new URLSearchParams({ site_id: siteId });
      const res = await fetch(`${API_URL}/api/v1/recipes/${recipeId}/dish?${params}`, {
        headers,
      });
      if (!res.ok) throw await apiError(t, res);
      return (await res.json()).data;
    },
  });
}

function useDebounced(value: string, ms: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/** Catalogue items matching `search`, from the list the org's recipes are costed against. The
 * last results stay on screen while the next are fetched. */
export function useInventorySearch(search: string) {
  const headers = useApiHeaders();
  const { t } = useTranslation();
  const settled = useDebounced(search, SEARCH_DEBOUNCE_MS);
  const list = useQuery<{ list: { id: string } }>({
    queryKey: ["resolved-inventory", "org"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/inventory/resolve`, { headers });
      if (!res.ok) throw await apiError(t, res);
      return (await res.json()).data;
    },
  });
  const listId = list.data?.list.id;
  return useQuery<InventoryItem[]>({
    queryKey: ["inventory-items-search", listId, settled],
    enabled: !!listId,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "50", search: settled });
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/items?${params}`, {
        headers,
      });
      if (!res.ok) throw await apiError(t, res);
      return (await res.json()).data;
    },
  });
}

export function useRecipeActions() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["recipe"] }),
      queryClient.invalidateQueries({ queryKey: ["menus"] }),
    ]);

  /** Saves the page; a change of content reaches every menu the dish is on. */
  const save = useMutation({
    mutationFn: async (v: { recipeId: string; body: DishSave }) => {
      const res = await fetch(`${API_URL}/api/v1/recipes/${v.recipeId}/dish`, {
        method: "PUT",
        headers,
        body: JSON.stringify(v.body),
      });
      if (!res.ok) throw await apiError(t, res);
    },
    onSettled: refresh,
    onError: showApiError,
  });

  /** Adds a dish by hand; resolves to its new rows' ids, one per menu, in order. */
  const add = useMutation({
    mutationFn: async (body: DishAdd): Promise<string[]> => {
      const res = await fetch(`${API_URL}/api/v1/recipes/dish`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await apiError(t, res);
      return (await res.json()).data;
    },
    onSettled: refresh,
    onError: showApiError,
  });

  return { save, add };
}
